import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLists, RowCheckbox, SelectionBar, ListFilter, ListDots } from '../components/Lists';
import BuyerStatusBadge, { BUYER_STATUSES, STATUS_META } from '../components/BuyerStatusBadge';
import { apiGet, apiSend, entitlementState } from '../utils/api';
import { LockedFeaturePanel } from '../components/EntitlementStates';
import { useAuth } from '../contexts/AuthContext';

function fmtMoney(n) {
  if (!n) return '—';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
}

function fmtDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtSqft(b) {
  const lo = b.buy_box_min_sqft, hi = b.buy_box_max_sqft;
  if (!lo && !hi) return '—';
  return `${lo ? lo.toLocaleString() : '?'}–${hi ? hi.toLocaleString() : '?'}`;
}

function Dash() { return <span style={{ color: 'var(--gray-500)' }}>—</span>; }

function Chips({ value }) {
  if (!value) return <Dash />;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {value.split(',').map((s, i) => (
        <span key={i} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: 'rgba(20, 80, 74,0.1)', color: 'var(--teal)', fontWeight: 500, whiteSpace: 'nowrap' }}>{s.trim()}</span>
      ))}
    </div>
  );
}

function NameCell({ buyer }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: `hsl(${(buyer.id * 47) % 360}, 55%, 35%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff' }}>
        {buyer.name.charAt(0)}
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', display: 'flex', alignItems: 'center', gap: 6 }}>
          {buyer.name}
          <ListDots type="buyer" id={buyer.id} />
          {(buyer.email_status === 'bouncing' || buyer.email_status === 'complained') && (
            <span title={`Email ${buyer.email_status === 'bouncing' ? 'bounced' : 'marked as spam'}${buyer.email_status_reason ? ` — ${buyer.email_status_reason}` : ''}. Excluded from email sends until re-verified.`}
                  style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(184,84,80,0.12)', color: '#B85450', fontWeight: 700, letterSpacing: '0.04em' }}>
              ⚠ {buyer.email_status === 'bouncing' ? 'BOUNCING' : 'SPAM'}
            </span>
          )}
        </div>
        {buyer.company && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{buyer.company}</div>}
      </div>
    </div>
  );
}

// Column registry for the Buyers list. `mandatory` cols can't be hidden. Cols with `sortVal` are
// click-sortable. DEFAULT_COLS is the standard view (name, email, phone, notes, date added); the rest
// are opt-in via the Columns picker (their data is always in the expanded row regardless).
const BUYER_COLUMNS = [
  { key: 'name',          label: 'Buyer',                 mandatory: true, sortVal: b => (b.name || '').toLowerCase(), render: b => <NameCell buyer={b} /> },
  { key: 'email',         label: 'Email',                 sortVal: b => (b.email || '').toLowerCase(), render: b => b.email ? <a href={`mailto:${b.email}`} onClick={e => e.stopPropagation()} style={{ color: 'var(--teal)', fontSize: 12 }}>{b.email}</a> : <Dash /> },
  { key: 'phone',         label: 'Phone',                 sortVal: b => (b.phone || ''), render: b => b.phone ? <a href={`tel:${b.phone}`} onClick={e => e.stopPropagation()} style={{ color: 'var(--gray-300)', fontSize: 12, whiteSpace: 'nowrap' }}>{b.phone}</a> : <Dash /> },
  { key: 'notes',         label: 'Notes',                 maxWidth: 420, render: b => <div style={{ fontSize: 12, color: 'var(--gray-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 400 }}>{b.notes || '—'}</div> },
  { key: 'date_added',    label: 'Date Added',            defaultDir: 'desc', sortVal: b => (b.created_at ? new Date(b.created_at).getTime() : 0), render: b => <span style={{ fontSize: 12, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>{fmtDate(b.created_at)}</span> },
  { key: 'status',        label: 'Status',                stopClick: true, render: (b, ctx) => <BuyerStatusBadge status={b.status || 'buyer'} onChange={ns => ctx.changeBuyerStatus(b.id, ns)} /> },
  { key: 'strategy',      label: 'Strategy',              render: b => <Chips value={b.buy_box_strategies} /> },
  { key: 'neighborhoods', label: 'Buy Box Neighborhoods', maxWidth: 200, render: b => <div style={{ fontSize: 12, color: 'var(--gray-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{b.buy_box_neighborhoods || '—'}</div> },
  { key: 'max_price',     label: 'Max Price',             defaultDir: 'desc', sortVal: b => (b.buy_box_max_price || 0), render: b => <span style={{ fontSize: 13, color: 'var(--white)', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtMoney(b.buy_box_max_price)}</span> },
  { key: 'sqft',          label: 'Sqft Range',            defaultDir: 'desc', sortVal: b => (b.buy_box_min_sqft || 0), render: b => <span style={{ fontSize: 12, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>{fmtSqft(b)}</span> },
  { key: 'past_purchases', label: 'Past Purchases',       align: 'center', defaultDir: 'desc', sortVal: b => (b.past_purchases || 0), render: b => <span style={{ fontSize: 14, fontWeight: 700, color: b.past_purchases >= 30 ? '#4A7C59' : b.past_purchases >= 15 ? 'var(--gold)' : 'var(--gray-300)' }}>{b.past_purchases || 0}</span> },
  { key: 'volume',        label: 'Volume',                defaultDir: 'desc', sortVal: b => (b.total_volume || 0), render: b => <span style={{ fontSize: 13, color: 'var(--teal)', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtMoney(b.total_volume)}</span> },
  { key: 'avg_close',     label: 'Avg Close',             defaultDir: 'asc', sortVal: b => (b.avg_close_days || 0), render: b => <span style={{ fontSize: 12, fontWeight: 600, color: b.avg_close_days <= 20 ? '#4A7C59' : b.avg_close_days <= 30 ? 'var(--gold)' : '#B85450' }}>{b.avg_close_days}d</span> },
  { key: 'pof',           label: 'POF',                   defaultDir: 'desc', sortVal: b => (b.proof_of_funds || 0), render: b => <span style={{ fontSize: 13, color: 'var(--gray-300)', whiteSpace: 'nowrap' }}>{fmtMoney(b.proof_of_funds)}</span> },
];
const DEFAULT_COLS = ['name', 'email', 'phone', 'notes', 'date_added'];

const STRATEGIES = ['All', 'Fix & Flip', 'Buy & Hold', 'BRRRR', 'STR'];

export default function Buyers() {
  const { plan } = useAuth();
  const navigate = useNavigate();
  const [buyers, setBuyers] = useState([]);
  const [locked, setLocked] = useState(null);   // EntitlementError when buyer_database isn't on the plan
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [strategy, setStrategy] = useState('All');
  const [selected, setSelected] = useState(null);
  // Status filter — drives the tabs at the top. 'all' = no filter.
  // 'needs_review' is a special pseudo-filter that hits the email_status flag, not status.
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusCounts, setStatusCounts] = useState({ counts: {}, total: 0, needs_review: 0 });
  // Custom Lists wiring — picks up the same memberSet store as the MLS / Dashboard pages.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [listFilter, setListFilter]   = useState(null);
  const { memberSet } = useLists();
  // Import Buyers + Add Buyer modals
  const [showImport, setShowImport] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  // Column visibility + sort, persisted per browser so a user's chosen layout sticks.
  const [visibleCols, setVisibleCols] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem('buyers_visible_cols')); if (Array.isArray(s) && s.length) return s; } catch {}
    return DEFAULT_COLS;
  });
  const [showColMenu, setShowColMenu] = useState(false);
  const [sortKey, setSortKey] = useState(() => { try { return JSON.parse(localStorage.getItem('buyers_sort'))?.key || 'date_added'; } catch { return 'date_added'; } });
  const [sortDir, setSortDir] = useState(() => { try { return JSON.parse(localStorage.getItem('buyers_sort'))?.dir || 'desc'; } catch { return 'desc'; } });

  const fetchBuyers = () => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (strategy !== 'All') params.append('strategy', strategy);
    // 'needs_review' is a special pseudo-status that filters by email_status, not status.
    if (statusFilter === 'needs_review') params.append('needs_review', '1');
    else if (statusFilter !== 'all') params.append('status', statusFilter);
    apiGet(`/api/buyers?${params}`)
      .then(data => { setBuyers(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(err => {
        // A 402 (e.g. demo's buyer_database=0) must NOT flow into buyers.reduce — keep buyers an
        // array and surface a locked panel instead of crashing.
        const s = entitlementState(err);
        if (s?.kind === 'locked') setLocked(s.error);
        setBuyers([]);
        setLoading(false);
      });
  };

  // Pull per-status counts for the tab badges. Refresh after a status change too.
  const fetchStatusCounts = () => {
    apiGet('/api/buyers/status-counts')
      .then(d => setStatusCounts(d && typeof d === 'object' ? d : { counts: {}, total: 0, needs_review: 0 }))
      .catch(() => {});
  };

  useEffect(() => { fetchBuyers(); }, [search, strategy, statusFilter]);
  useEffect(() => { fetchStatusCounts(); }, []);

  // Inline status change — PUT to backend, optimistic update + refresh counts.
  async function changeBuyerStatus(buyerId, newStatus) {
    // Optimistic update
    setBuyers(prev => prev.map(b => b.id === buyerId ? { ...b, status: newStatus } : b));
    try {
      const r = await fetch(`/api/buyers/${buyerId}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!r.ok) throw new Error('status update failed');
      fetchStatusCounts(); // refresh tab badges
      // If the change moves the buyer out of the current tab filter, also refresh the list
      if (statusFilter !== 'all' && newStatus !== statusFilter) fetchBuyers();
    } catch (err) {
      // Revert on failure
      fetchBuyers();
      console.error('Failed to update buyer status:', err);
    }
  }

  const totalVolume = buyers.reduce((s, b) => s + (b.total_volume || 0), 0);
  const avgClose = buyers.length ? Math.round(buyers.reduce((s, b) => s + b.avg_close_days, 0) / buyers.length) : 0;

  // Persist column layout + sort choice.
  useEffect(() => { try { localStorage.setItem('buyers_visible_cols', JSON.stringify(visibleCols)); } catch {} }, [visibleCols]);
  useEffect(() => { try { localStorage.setItem('buyers_sort', JSON.stringify({ key: sortKey, dir: sortDir })); } catch {} }, [sortKey, sortDir]);

  // Columns to render (registry order; mandatory cols always in). Toggling / sorting handlers.
  const cols = useMemo(() => BUYER_COLUMNS.filter(c => c.mandatory || visibleCols.includes(c.key)), [visibleCols]);
  function toggleCol(key) {
    setVisibleCols(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
  }
  function onSort(key) {
    const col = BUYER_COLUMNS.find(c => c.key === key);
    if (!col?.sortVal) return;
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(col.defaultDir || 'asc'); }
  }

  // Apply list-membership filter (if any), then sort by the active column + direction.
  const visibleBuyers = useMemo(() => {
    let list = buyers;
    if (listFilter) {
      const members = memberSet(listFilter);  // Set of "type:id" strings
      list = buyers.filter(b => members.has(`buyer:${b.id}`));
    }
    const col = BUYER_COLUMNS.find(c => c.key === sortKey && c.sortVal) || BUYER_COLUMNS.find(c => c.key === 'date_added');
    const sv = col.sortVal;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const va = sv(a), vb = sv(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return ((a.id || 0) - (b.id || 0)) * dir;  // id tiebreaker (same-batch import shares a timestamp)
    });
  }, [buyers, listFilter, memberSet, sortKey, sortDir]);

  // Items shape expected by SelectionBar / AddToListMenu
  const selectionItems = useMemo(
    () => Array.from(selectedIds).map(id => ({ item_type: 'buyer', item_id: String(id) })),
    [selectedIds],
  );

  function toggleBuyerSelected(id, on) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }

  const allVisibleSelected = visibleBuyers.length > 0 && visibleBuyers.every(b => selectedIds.has(b.id));
  function toggleSelectAllVisible(on) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      visibleBuyers.forEach(b => on ? next.add(b.id) : next.delete(b.id));
      return next;
    });
  }

  // Feature not on this org's plan (e.g. demo) → friendly panel, never a crash.
  if (locked) {
    return (
      <div>
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--white)', marginBottom: 6, letterSpacing: '-0.02em' }}>Buyers Database</h1>
          <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>Qualified investors with verified buy boxes and purchase history</p>
        </div>
        <LockedFeaturePanel feature={locked.feature || 'buyer_database'} plan={locked.plan || plan?.key} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--white)', marginBottom: 6, letterSpacing: '-0.02em' }}>Buyers Database</h1>
          <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>Qualified investors with verified buy boxes and purchase history</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setShowAdd(true)}
            style={{
              padding: '9px 16px', borderRadius: 8, cursor: 'pointer',
              background: 'transparent', color: 'var(--teal)', border: '1px solid var(--teal)',
              fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Buyer
          </button>
          <button
            onClick={() => setShowImport(true)}
            style={{
              padding: '9px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: 'var(--teal)', color: '#fff', fontSize: 13, fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            Import Buyers
          </button>
        </div>
      </div>

      {showImport && (
        <ImportBuyersModal
          onClose={() => setShowImport(false)}
          onImported={() => { fetchBuyers(); fetchStatusCounts(); }}
        />
      )}

      {showAdd && (
        <AddBuyerModal
          onClose={() => setShowAdd(false)}
          onAdded={() => { fetchBuyers(); fetchStatusCounts(); }}
        />
      )}

      {/* Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Buyers', value: statusCounts.total || buyers.length, color: 'var(--white)' },
          { label: 'Network Volume', value: fmtMoney(totalVolume), color: 'var(--teal)' },
          { label: 'Avg Close Days', value: `${avgClose}d`, color: 'var(--gold)' },
          // "Marketable" = onboarded + buyer (the only statuses eligible to receive blasts).
          { label: 'Marketable Buyers', value: (statusCounts.marketable_total || 0).toLocaleString(), color: '#4A7C59' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '16px 24px', flex: 1, minWidth: 150 }}>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Status tabs — primary grouping. Each tab counts how many buyers are in that status.
          "Needs Review" is a separate filter for buyers with bouncing/complained emails —
          shown last + emphasized in brick red when non-zero so you can't miss it. */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <StatusTab
          active={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')}
          label="All"
          count={statusCounts.total || 0}
          color="var(--gray-300)"
        />
        {BUYER_STATUSES.map(s => {
          const meta = STATUS_META[s];
          return (
            <StatusTab
              key={s}
              active={statusFilter === s}
              onClick={() => setStatusFilter(s)}
              label={meta.label}
              count={statusCounts.counts?.[s] || 0}
              color={meta.color}
              title={meta.desc}
            />
          );
        })}
        {/* Email deliverability flag — separate from status. Always shown; gets visual
            emphasis only when there's something to review. */}
        <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--navy-border)', margin: '0 4px' }} />
        <StatusTab
          active={statusFilter === 'needs_review'}
          onClick={() => setStatusFilter('needs_review')}
          label="⚠ Needs Review"
          count={statusCounts.needs_review || 0}
          color={(statusCounts.needs_review || 0) > 0 ? '#B85450' : 'var(--gray-300)'}
          title="Buyers whose last email send hard-bounced or was marked as spam. Excluded from future sends until you fix and re-verify the address."
        />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
          <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, company, email..."
            style={{ ...inputStyle, paddingLeft: 36, width: '100%' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {STRATEGIES.map(s => (
            <button key={s} onClick={() => setStrategy(s)} style={{
              padding: '7px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 500,
              background: strategy === s ? 'var(--teal)' : 'var(--navy-card)',
              color: strategy === s ? '#fff' : 'var(--gray-500)',
              border: strategy === s ? 'none' : '1px solid var(--navy-border)',
              transition: 'all 0.15s',
            }}>
              {s}
            </button>
          ))}
        </div>
        {/* Filter buyers by membership in a custom list */}
        <ListFilter value={listFilter} onChange={setListFilter} />
        {/* Columns picker — choose which fields show as columns (persisted). */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowColMenu(v => !v)}
            title="Choose columns"
            style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy-card)', color: 'var(--gray-300)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" />
            </svg>
            Columns
          </button>
          {showColMenu && (
            <>
              <div onClick={() => setShowColMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
              <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 41, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: 10, minWidth: 210, boxShadow: '0 10px 30px rgba(0,0,0,0.35)' }}>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, padding: '0 4px' }}>Show columns</div>
                {BUYER_COLUMNS.map(c => (
                  <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 4px', fontSize: 13, color: 'var(--gray-300)', cursor: c.mandatory ? 'default' : 'pointer', opacity: c.mandatory ? 0.55 : 1 }}>
                    <input type="checkbox" checked={c.mandatory || visibleCols.includes(c.key)} disabled={c.mandatory} onChange={() => toggleCol(c.key)} />
                    {c.label}
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bulk-action bar — appears when 1+ buyers are checked */}
      <SelectionBar
        count={selectedIds.size}
        items={selectionItems}
        onClear={() => setSelectedIds(new Set())}
      />

      {/* Table */}
      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--navy-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>All Buyers</div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
            {visibleBuyers.length} {listFilter ? `in list (of ${buyers.length})` : 'results'}
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--navy-border)' }}>
                {/* Select-all checkbox header */}
                <th style={{ padding: '10px 8px 10px 16px', width: 36, background: 'rgba(255,255,255,0.02)' }}>
                  <RowCheckbox checked={allVisibleSelected} onChange={toggleSelectAllVisible} title="Select all visible" />
                </th>
                {cols.map(c => {
                  const active = sortKey === c.key;
                  const sortable = !!c.sortVal;
                  return (
                    <th key={c.key}
                        onClick={sortable ? () => onSort(c.key) : undefined}
                        style={{ padding: '10px 16px', textAlign: c.align || 'left', fontSize: 11, fontWeight: 700, color: active ? 'var(--teal)' : 'var(--gray-500)', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap', background: 'rgba(26, 23, 20, 0.025)', cursor: sortable ? 'pointer' : 'default', userSelect: 'none' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {c.label}
                        {sortable && <span style={{ fontSize: 9, opacity: active ? 1 : 0.35 }}>{active ? (sortDir === 'desc' ? '▼' : '▲') : '↕'}</span>}
                      </span>
                    </th>
                  );
                })}
                {/* chevron column */}
                <th style={{ padding: '10px 16px', width: 32, background: 'rgba(26, 23, 20, 0.025)' }} />
              </tr>
            </thead>
            <tbody>
              {visibleBuyers.map((buyer) => (
                <React.Fragment key={buyer.id}>
                  <tr
                    style={{
                      borderBottom: '1px solid rgba(232, 227, 218, 0.7)', cursor: 'pointer', transition: 'background 0.1s',
                      background: selectedIds.has(buyer.id) ? 'rgba(20, 80, 74, 0.06)' : 'transparent',
                    }}
                    onMouseOver={e => e.currentTarget.style.background = selectedIds.has(buyer.id) ? 'rgba(20, 80, 74, 0.10)' : 'rgba(26, 23, 20, 0.03)'}
                    onMouseOut={e => e.currentTarget.style.background = selectedIds.has(buyer.id) ? 'rgba(20, 80, 74, 0.06)' : 'transparent'}
                    onClick={() => setSelected(selected === buyer.id ? null : buyer.id)}
                  >
                    {/* Per-row checkbox — clicks here don't bubble to row's expand-toggle */}
                    <td style={{ padding: '14px 8px 14px 16px', width: 36 }} onClick={e => e.stopPropagation()}>
                      <RowCheckbox checked={selectedIds.has(buyer.id)} onChange={(on) => toggleBuyerSelected(buyer.id, on)} />
                    </td>
                    {cols.map(c => (
                      <td
                        key={c.key}
                        onClick={c.key === 'name'
                          ? (e => { e.stopPropagation(); navigate(`/buyers/${buyer.id}`); })
                          : (c.stopClick ? (e => e.stopPropagation()) : undefined)}
                        title={c.key === 'name' ? 'Open contact page' : undefined}
                        style={{ padding: '14px 16px', textAlign: c.align || 'left', maxWidth: c.maxWidth, cursor: c.key === 'name' ? 'pointer' : undefined }}
                      >
                        {c.render(buyer, { changeBuyerStatus })}
                      </td>
                    ))}
                    {/* expand chevron */}
                    <td style={{ padding: '14px 16px' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gray-500)" strokeWidth="2" style={{ transform: selected === buyer.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </td>
                  </tr>

                  {/* Expanded row — full detail (always shows every field, regardless of visible columns) */}
                  {selected === buyer.id && (
                    <tr style={{ borderBottom: '1px solid var(--navy-border)' }}>
                      <td colSpan={cols.length + 2} style={{ padding: 0 }}>
                        <div style={{ padding: '16px 20px 20px', background: 'rgba(26, 23, 20, 0.025)' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                            <Link to={`/buyers/${buyer.id}`} style={{ fontSize: 12, fontWeight: 600, color: 'var(--teal)', textDecoration: 'none' }}>Open full contact page →</Link>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 16 }}>
                            <div>
                              <div style={detailLabel}>Contact</div>
                              <div style={{ fontSize: 13, color: 'var(--gray-300)', lineHeight: 1.7 }}>
                                <div><a href={`mailto:${buyer.email}`} style={{ color: 'var(--teal)' }}>{buyer.email || '—'}</a></div>
                                <div>{buyer.phone || '—'}</div>
                                {buyer.company && <div style={{ color: 'var(--gray-500)' }}>{buyer.company}</div>}
                              </div>
                              {(buyer.email_status === 'bouncing' || buyer.email_status === 'complained') && (
                                <EmailReverifyForm buyer={buyer} onSaved={() => { fetchBuyers(); fetchStatusCounts(); }} />
                              )}
                            </div>
                            <div>
                              <div style={detailLabel}>Status</div>
                              <div onClick={e => e.stopPropagation()}>
                                <BuyerStatusBadge status={buyer.status || 'buyer'} onChange={ns => changeBuyerStatus(buyer.id, ns)} />
                              </div>
                              <div style={{ ...detailLabel, marginTop: 14 }}>Target Areas</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {buyer.buy_box_neighborhoods
                                  ? buyer.buy_box_neighborhoods.split(',').map((n, i) => (
                                    <span key={i} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10, background: 'var(--navy-border)', color: 'var(--gray-300)' }}>{n.trim()}</span>
                                  ))
                                  : <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>—</span>}
                              </div>
                            </div>
                            <div>
                              <div style={detailLabel}>Notes</div>
                              <div style={{ fontSize: 12, color: 'var(--gray-300)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{buyer.notes || '—'}</div>
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 14, marginBottom: 16 }}>
                            {[
                              ['Strategy', buyer.buy_box_strategies || '—'],
                              ['Max Price', fmtMoney(buyer.buy_box_max_price)],
                              ['Sqft Range', fmtSqft(buyer)],
                              ['Past Purchases', buyer.past_purchases || 0],
                              ['Volume', fmtMoney(buyer.total_volume)],
                              ['Avg Close', `${buyer.avg_close_days}d`],
                              ['Proof of Funds', fmtMoney(buyer.proof_of_funds)],
                            ].map(([k, v]) => (
                              <div key={k}>
                                <div style={detailLabel}>{k}</div>
                                <div style={{ fontSize: 13, color: 'var(--white)', fontWeight: 600 }}>{v}</div>
                              </div>
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <a href={`mailto:${buyer.email}`} style={{ ...smallBtnStyle('var(--teal)'), textDecoration: 'none' }}>Email Buyer</a>
                            {buyer.phone && <a href={`tel:${buyer.phone}`} style={{ ...smallBtnStyle('var(--gold)'), textDecoration: 'none' }}>Call {buyer.phone}</a>}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {visibleBuyers.length === 0 && !loading && (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--gray-500)' }}>
              {listFilter
                ? 'No buyers in the selected list. Pick a different list or clear the filter.'
                : 'No buyers match your search. Try adjusting filters.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  padding: '9px 14px',
  background: 'var(--navy-card)',
  border: '1px solid var(--navy-border)',
  borderRadius: 8,
  color: 'var(--white)',
  fontSize: 13,
  outline: 'none',
};

const detailLabel = { fontSize: 11, color: 'var(--gray-500)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' };

function smallBtnStyle(color) {
  return { padding: '6px 14px', borderRadius: 7, border: `1px solid ${color}40`, background: `${color}15`, color, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
}

// Inline form for fixing a bouncing email + re-verifying the buyer. Shown inside the
// expanded row when buyer.email_status is 'bouncing' or 'complained'. Optional new
// email — leave blank to mark as verified without changing the address (e.g. if the
// bounce was a transient delivery error and the address is actually fine).
function EmailReverifyForm({ buyer, onSaved }) {
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const body = newEmail.trim() ? { email: newEmail.trim() } : {};
      const r = await fetch(`/api/buyers/${buyer.id}/email-verify`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || `HTTP ${r.status}`);
      setBusy(false);
      onSaved && onSaved();
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: 'rgba(184,84,80,0.06)', border: '1px solid rgba(184,84,80,0.25)' }}>
      <div style={{ fontSize: 11, color: '#B85450', fontWeight: 700, marginBottom: 6 }}>
        ⚠ Email {buyer.email_status === 'bouncing' ? 'bouncing' : 'flagged as spam'}
      </div>
      {buyer.email_status_reason && (
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 8, fontStyle: 'italic' }}>
          "{buyer.email_status_reason}"
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--gray-300)', marginBottom: 6, lineHeight: 1.5 }}>
        Excluded from email sends until re-verified. Enter a corrected address (or leave blank to re-verify the current one), then click below.
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          value={newEmail}
          onChange={e => setNewEmail(e.target.value)}
          placeholder={buyer.email || 'corrected@example.com'}
          disabled={busy}
          style={{ flex: 1, padding: '6px 9px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 12, outline: 'none' }}
        />
        <button onClick={submit} disabled={busy} style={{ padding: '6px 12px', borderRadius: 6, background: 'var(--teal)', border: 'none', color: '#fff', fontSize: 11, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? '…' : 'Mark Verified'}
        </button>
      </div>
      {err && <div style={{ fontSize: 11, color: '#B85450', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

// Add a single buyer manually. Lands in the creating user's own book (owner-scoped on the backend).
function AddBuyerModal({ onClose, onAdded }) {
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '',
    buy_box_neighborhoods: '', buy_box_strategies: '', buy_box_max_price: '',
    status: 'buyer', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit() {
    if (!form.name.trim()) { setErr('Name is required'); return; }
    setBusy(true); setErr(null);
    try {
      await apiSend('/api/buyers', { body: form });
      onAdded && onAdded();
      onClose();
    } catch (e) {
      setErr(e.message || 'Failed to add buyer');
      setBusy(false);
    }
  }

  const field = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 13, outline: 'none' };
  const label = { fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5, display: 'block' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '88vh', overflowY: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--white)' }}>Add Buyer</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={label}>Name *</label>
            <input value={form.name} onChange={set('name')} autoFocus placeholder="Jane Investor" style={field} />
          </div>
          <div><label style={label}>Company</label><input value={form.company} onChange={set('company')} placeholder="Acme Homes" style={field} /></div>
          <div>
            <label style={label}>Status</label>
            <select value={form.status} onChange={set('status')} style={field}>
              {BUYER_STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
            </select>
          </div>
          <div><label style={label}>Email</label><input value={form.email} onChange={set('email')} placeholder="jane@acme.com" style={field} /></div>
          <div><label style={label}>Phone</label><input value={form.phone} onChange={set('phone')} placeholder="(602) 555-0100" style={field} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={label}>Buy Box Neighborhoods / Areas</label><input value={form.buy_box_neighborhoods} onChange={set('buy_box_neighborhoods')} placeholder="Gilbert, Mesa, Queen Creek, 85234" style={field} /></div>
          <div><label style={label}>Strategy</label><input value={form.buy_box_strategies} onChange={set('buy_box_strategies')} placeholder="Fix & Flip" style={field} /></div>
          <div><label style={label}>Max Price</label><input value={form.buy_box_max_price} onChange={set('buy_box_max_price')} placeholder="$450k" style={field} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={label}>Notes</label><textarea value={form.notes} onChange={set('notes')} rows={3} placeholder="Lead temp: Hot. Pays cash, closes in 10 days." style={{ ...field, resize: 'vertical', fontFamily: 'inherit' }} /></div>
        </div>

        {err && <div style={{ marginTop: 12, fontSize: 12, color: '#B85450' }}>{err}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-300)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={submit} disabled={busy || !form.name.trim()} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: busy || !form.name.trim() ? 'not-allowed' : 'pointer', opacity: busy || !form.name.trim() ? 0.6 : 1 }}>
            {busy ? 'Adding…' : 'Add Buyer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Import Buyers — paste rows or upload a CSV. Imported buyers land in the importing user's
// own book (owner-scoped on the backend), so one agent's list never shows up in another's view.
function ImportBuyersModal({ onClose, onImported }) {
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  function onFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ''));
    reader.readAsText(file);
  }

  async function submit() {
    setBusy(true); setErr(null); setResult(null);
    try {
      const data = await apiSend('/api/buyers/import', { body: { text } });
      setResult(data);
      onImported && onImported();
    } catch (e) {
      setErr(e.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  }

  // Quote-aware row estimate: count newlines that aren't inside a quoted CSV field, so long
  // free-text cells with embedded line breaks don't inflate the count. Minus the header row.
  const estRows = useMemo(() => {
    let rows = 0, inQuotes = false, hasContent = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (ch === '"') inQuotes = !inQuotes;
      else if (ch === '\n' && !inQuotes) { if (hasContent) rows++; hasContent = false; }
      else if (ch !== '\r' && ch !== ' ') hasContent = true;
    }
    if (hasContent) rows++;
    return rows > 1 ? rows - 1 : rows;
  }, [text]);

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, width: '100%', maxWidth: 620, maxHeight: '86vh', overflowY: 'auto', padding: 24 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--white)' }}>Import Buyers</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--gray-500)', lineHeight: 1.6, marginBottom: 16 }}>
          Upload a CSV or paste rows. The first row should be a header — we auto-map common columns like{' '}
          <code style={codeChip}>Name</code>, <code style={codeChip}>Email</code>, <code style={codeChip}>Phone</code>,{' '}
          <code style={codeChip}>Company</code>, <code style={codeChip}>Areas/Zips</code>, <code style={codeChip}>Strategy</code>,{' '}
          <code style={codeChip}>Max Price</code>, <code style={codeChip}>Notes</code>. Only <b>Name</b> is required.
          These buyers are added to <b>your</b> list and marked marketable.
        </p>

        {!result && (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={{ ...uploadBtn, cursor: 'pointer' }}>
                <input type="file" accept=".csv,.tsv,.txt,text/csv" onChange={onFile} style={{ display: 'none' }} />
                {fileName ? `📄 ${fileName}` : 'Choose CSV file…'}
              </label>
            </div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '10px 0 6px' }}>
              …or paste rows
            </div>
            <textarea
              value={text}
              onChange={e => { setText(e.target.value); setFileName(''); }}
              placeholder={'Name,Email,Phone,Company,Areas,Strategy,Max Price,Notes\nJane Investor,jane@acme.com,512-555-0100,Acme Homes,"78704, 78745",Fix & Flip,$450k,Lead temp: Hot'}
              rows={9}
              style={{ width: '100%', boxSizing: 'border-box', padding: 12, borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 12, fontFamily: 'ui-monospace, Menlo, monospace', outline: 'none', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                {text.trim() ? `~${estRows} buyer${estRows === 1 ? '' : 's'} to import` : ' '}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-300)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button
                  onClick={submit}
                  disabled={busy || !text.trim()}
                  style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: busy || !text.trim() ? 'not-allowed' : 'pointer', opacity: busy || !text.trim() ? 0.6 : 1 }}
                >
                  {busy ? 'Importing…' : 'Import'}
                </button>
              </div>
            </div>
            {err && <div style={{ marginTop: 12, fontSize: 12, color: '#B85450' }}>{err}</div>}
          </>
        )}

        {result && (
          <div>
            <div style={{ padding: 16, borderRadius: 10, background: 'rgba(74,124,89,0.08)', border: '1px solid rgba(74,124,89,0.3)', marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#4A7C59', marginBottom: 8 }}>✓ Import complete</div>
              <div style={{ fontSize: 13, color: 'var(--gray-300)', lineHeight: 1.9 }}>
                <div><b style={{ color: 'var(--white)' }}>{result.inserted}</b> added</div>
                {result.skipped > 0 && <div><b style={{ color: 'var(--white)' }}>{result.skipped}</b> skipped (already in your list)</div>}
                {result.errors && result.errors.length > 0 && (
                  <div style={{ color: '#B85450' }}><b>{result.errors.length}</b> failed</div>
                )}
                {result.mapped_fields && result.mapped_fields.length > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6 }}>
                    Mapped columns: {result.mapped_fields.join(', ')}
                  </div>
                )}
              </div>
            </div>
            {result.errors && result.errors.length > 0 && (
              <div style={{ fontSize: 11, color: 'var(--gray-500)', maxHeight: 120, overflowY: 'auto', marginBottom: 12 }}>
                {result.errors.slice(0, 10).map((e, i) => <div key={i}>• {e.name}: {e.error}</div>)}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const codeChip = { fontSize: 11, padding: '1px 5px', borderRadius: 4, background: 'var(--navy-border)', color: 'var(--gray-300)' };
const uploadBtn = { display: 'inline-block', padding: '10px 16px', borderRadius: 8, border: '1px dashed var(--navy-border)', background: 'var(--navy)', color: 'var(--gray-300)', fontSize: 13, fontWeight: 600 };

// Status filter tab — single pill with label + count badge. Active state borrows the
// status's own color so the row of tabs reads as a color-coded legend at a glance.
function StatusTab({ active, onClick, label, count, color, title }) {
  return (
    <button
      onClick={onClick}
      title={title || ''}
      style={{
        padding: '6px 12px', borderRadius: 999, border: `1px solid ${active ? color : 'var(--navy-border)'}`,
        background: active ? `${color}1F` : 'transparent',
        color: active ? color : 'var(--gray-500)',
        fontSize: 12, fontWeight: 700, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        transition: 'all 0.12s',
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, opacity: active ? 1 : 0.6 }} />
      {label}
      <span style={{ fontSize: 11, opacity: 0.7, fontWeight: 600 }}>· {count.toLocaleString()}</span>
    </button>
  );
}
